import {useCallback} from 'react';
import {useSelector, useStore} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {stateToBackendEventRepresentation} from '~/CreateFabricInvitation/api/event';
import {eventInEditMode} from '~/CreateFabricInvitation/reducers/details/selectors';
import {useRequestLogin} from '~/common/fabric/Card/components/LoginModal/hooks/useRequestLogin';
import {SET_EVITE_EVENT} from '~/common/fabric/reducers/eviteEvent/constants';
import {getLoggedUserId} from '~/common/fabric/User/reducers/user';
import {useSaveEventDraftMutation} from '~/OneStepCreateInvitation/api/endpoints/event';
import {toastAlert} from '~library/atoms/Toast';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

export const useEventSave = () => {
  const store = useStore();
  const history = useHistory();
  const {requestLogin} = useRequestLogin();
  const isLoggedIn = useSelector(getLoggedUserId);
  const isInEditMode = useSelector(eventInEditMode);
  const [sharedSaveEventDraft, {isLoading, isSuccess, isError}] = useSaveEventDraftMutation({
    fixedCacheKey: 'shared-save-event-draft',
  });
  const [finshAndSaveEventDraft] = useSaveEventDraftMutation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const onSave = useCallback(
    async (finish = false) => {
      const body = await stateToBackendEventRepresentation(store.getState(), false);
      const saveFn = finish ? finshAndSaveEventDraft : sharedSaveEventDraft;

      const response = await saveFn(body);
      if (response?.error) {
        toastAlert(response.error.data?.message || 'Failed to save invitation', {
          icon: <AlertLineIcon ratio={1.5} />,
          type: 'error',
          position: 'top-right',
        });
        return false;
      }

      const {data} = response;

      if (!window.evite_event) {
        window.evite_event = data.event;
      }

      store.dispatch({type: SET_EVITE_EVENT, payload: data.event});

      if (history.location.pathname.includes('/create')) {
        history.replace({
          pathname: `/${data.event.id}/customize`,
          hash: history.location.hash,
          search: history.location.search,
        });
      }

      return data;
    },
    [store, finshAndSaveEventDraft, sharedSaveEventDraft]
  );

  const autoSave = useCallback(
    async (finish = false, alreadyLoggedIn = false) => {
      // alreadyLoggedIn is a hack, since requestLogin does not update the state,
      // so calling onSave after requestLogin won't update isLoggedIn here, so
      // the login modal will reappear/stay-open.
      if (!alreadyLoggedIn && !isLoggedIn) {
        const loggedIn = await requestLogin();

        if (!loggedIn) {
          return false;
        }
      }
      return !isInEditMode && onSave(finish);
    },
    [isInEditMode, isLoggedIn, onSave, requestLogin]
  );

  /* DEPRECATED */
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const onFinish = useCallback(async () => {
    const data = await onSave(true);

    if (data) {
      // probably should figure out if saving is needed before this call
      await onSave();
      history.push({
        pathname: `/${data.event.id}/guests`,
        search: history.location.search,
      });
    }
  }, [onSave]);

  return {autoSave, onSave, onFinish, isLoading, isSuccess, isError};
};
